import { CSSProperties, useReducer } from "react";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { BeneficiaryType, Flow, GoalType } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { Dollar2, Education2, Home2, Rain } from "@entry/goalIcons";
import { EntryGlassOption } from "@entry/ui";
import { useGoalFlow } from "@hooks";
import { Translations, i18n } from "@i18n";
import { palette } from "@layout";
import { goalTypeLabel } from "./content";
import { useGoalTypeScreenFlow } from "./flow";
import { GoalTypeSelectionAction, GoalTypeSelectionState, GoalTypeSelectionType } from "./types";

const initialState: GoalTypeSelectionState = {
  home: false,
  education: false,
  emergencyFund: false,
  highYieldSavings: false,
  longTerm: false,
  vacation: false,
  trumpSidecar: false,
  custom: false,
};

const reducer = (state: GoalTypeSelectionState, action: GoalTypeSelectionAction) => {
  return { ...initialState, [action.type]: !state[action.type] };
};

const iconContainerStyle = (selected: boolean): CSSProperties => ({
  width: 40,
  height: 40,
  borderRadius: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: selected ? palette.lightbrown[100] : palette.black[20],
});

const pngIconStyle: CSSProperties = {
  width: 30,
  height: 30,
};

export const GoalTypeRoute = () => {
  const [
    { home, education, longTerm, emergencyFund, highYieldSavings, custom, vacation, trumpSidecar },
    dispatch,
  ] = useReducer(reducer, initialState);

  const {
    goalProps: { flow, beneficiaryType },
  } = useGoalFlow();

  const isChildFlow = beneficiaryType === BeneficiaryType.Child;
  const canSelectTrumpSidecar = isChildFlow && flow !== Flow.EditGoal;

  const goalType = (() => {
    if (education) return GoalType.Education;
    if (home) return GoalType.House;
    if (longTerm) return GoalType.GenerationalWealth;
    if (emergencyFund) return GoalType.EmergencyFund;
    if (highYieldSavings) return GoalType.HighYieldSavings;
    if (trumpSidecar) return GoalType.TrumpAccountSidecar;
    if (custom) return GoalType.Custom;

    return GoalType.GenerationalWealth;
  })();

  const { handleSubmit, loading } = useGoalTypeScreenFlow({ goalType });

  const disabled = [
    home,
    education,
    emergencyFund,
    highYieldSavings,
    longTerm,
    vacation,
    trumpSidecar,
    custom,
  ].every((value) => !value);

  const title =
    flow === Flow.EditGoal
      ? i18n.t(Translations.GoalTypeRouteEditTitle)
      : i18n.t(Translations.EntryGoalTypeTitle);

  const subtitle =
    flow === Flow.EditGoal
      ? i18n.t(Translations.GoalTypeRouteEditSubtitle)
      : i18n.t(Translations.GoalTypeRouteSubtitle);

  const props: OnboardingLayoutProps = {
    title,
    subtitle,
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: i18n.t(Translations.GoalTypeRoutePrimaryCTA),
        onClick: handleSubmit,
        loading,
        disabled,
      },
    },
  };

  const selectEducation = () => {
    dispatch({ type: GoalTypeSelectionType.Education });
  };

  const selectLongTerm = () => {
    dispatch({ type: GoalTypeSelectionType.LongTerm });
  };

  const selectHome = () => {
    dispatch({ type: GoalTypeSelectionType.Home });
  };

  const selectEmergencyFund = () => {
    dispatch({ type: GoalTypeSelectionType.EmergencyFund });
  };

  const selectHighYieldSavings = () => {
    dispatch({ type: GoalTypeSelectionType.HighYieldSavings });
  };

  const selectTrumpSidecar = () => {
    dispatch({ type: GoalTypeSelectionType.TrumpSidecar });
  };

  const selectCustom = () => {
    dispatch({ type: GoalTypeSelectionType.Custom });
  };

  return (
    <OnboardingLayout {...props}>
      <EntryGlassOption
        title={goalTypeLabel[GoalType.Education]}
        selected={education}
        onSelect={selectEducation}
        hideIndicator
        leftElement={
          <Education2
            fill={education ? palette.white[100] : undefined}
            background={education ? palette.lightbrown[100] : undefined}
          />
        }
      />
      <Spacing size={SpacingSizes.M} />

      <EntryGlassOption
        title={goalTypeLabel[GoalType.GenerationalWealth]}
        selected={longTerm}
        onSelect={selectLongTerm}
        hideIndicator
        leftElement={
          <Dollar2
            fill={longTerm ? palette.white[100] : undefined}
            background={longTerm ? palette.lightbrown[100] : undefined}
          />
        }
      />
      <Spacing size={SpacingSizes.M} />

      <EntryGlassOption
        title={goalTypeLabel[GoalType.House]}
        selected={home}
        onSelect={selectHome}
        hideIndicator
        leftElement={
          <Home2
            fill={home ? palette.white[100] : undefined}
            background={home ? palette.lightbrown[100] : undefined}
          />
        }
      />
      <Spacing size={SpacingSizes.M} />

      <EntryGlassOption
        title={goalTypeLabel[GoalType.EmergencyFund]}
        selected={emergencyFund}
        onSelect={selectEmergencyFund}
        hideIndicator
        leftElement={
          <Rain
            fill={emergencyFund ? palette.white[100] : undefined}
            background={emergencyFund ? palette.lightbrown[100] : undefined}
          />
        }
      />
      <Spacing size={SpacingSizes.M} />

      <EntryGlassOption
        title={goalTypeLabel[GoalType.HighYieldSavings]}
        selected={highYieldSavings}
        onSelect={selectHighYieldSavings}
        hideIndicator
        leftElement={
          <div style={iconContainerStyle(highYieldSavings)}>
            <img
              src={
                highYieldSavings
                  ? "/images/entry/hys-selected.png"
                  : "/images/entry/hys-unselected.png"
              }
              alt=""
              style={pngIconStyle}
            />
          </div>
        }
      />
      <Spacing size={SpacingSizes.M} />

      {canSelectTrumpSidecar && (
        <>
          <EntryGlassOption
            title={goalTypeLabel[GoalType.TrumpAccountSidecar]}
            selected={trumpSidecar}
            onSelect={selectTrumpSidecar}
            hideIndicator
            leftElement={
              <div style={iconContainerStyle(trumpSidecar)}>
                <img
                  src={
                    trumpSidecar
                      ? "/images/entry/trump-sidecar-selected.png"
                      : "/images/entry/trump-sidecar-unselected.png"
                  }
                  alt=""
                  style={pngIconStyle}
                />
              </div>
            }
          />
          <Spacing size={SpacingSizes.M} />
        </>
      )}

      <EntryGlassOption
        title={goalTypeLabel[GoalType.Custom]}
        selected={custom}
        onSelect={selectCustom}
        hideIndicator
      />
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
